import React, { useEffect, useState, useMemo } from 'react';
import { ModalsKeys } from 'global';
import { Button } from '@mui/material';
import { logError } from 'utils/logger';
import { useModal } from 'hooks/useModal';
import { Build } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { FinanceProgram } from '@trnsact/trnsact-shared-types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { PanelForm } from './PanelForm';
import { GET_EQUIPMENT } from '../../../api';
import { ApiContainer } from '../APIContainer';
import { useSaleMode } from '../../../context';
import { PanelFormStyled } from './PanelFormStyled';
import { PanelFormActions } from './PanelFormActions';
import { EditAdapter } from '../EditAdapter';
import { deskingActions, deskingSelectors } from '../../../model';
import { getCcEmailPreSelectedValue } from '../../../lib/convertVOToInitialFormValue';
import { convertInvoiceToSaleDetailsFormValues, getFormDefaultValues, setProgramDeps } from '../../../lib';
import { EquipmentsResponse, EquipmentsVariables, ProposalProductsResponse } from '../../../api/types';
import {
  CcEmailOption,
  ContactForm,
  CreateProposalFinanceExistingOptions,
  Equipment,
  FinanceProgramFormValues,
  PanelFormContainerProps,
  SaleType,
  SubmitOptions,
} from '../../../types';

export const PanelFormContainer = (props: PanelFormContainerProps) => {
  const {
    actions,
    accountId,
    userEmail,
    vo = null,
    invoiceAction,
    invoice = null,
    customSections,
    onSubmitDesking,
    decisionId = null,
    proposalId = null,
    creditApplication,
    isProposalLoading,
    configurationByAccount,
    formFieldsConfiguration,
    initialLenderProfileId = null,
    initialFinanceProgramId = null,
    onCreateProposalProduct,
    allAvailableCcEmailOptions = [],
    isSupportBehaviorSetting = false,
    isSupportSaleDetailsUpdating = false,
    externalDealInfo = null,
  } = props;

  const { onChangeSaleType } = useSaleMode();
  const { handleOpen: createCustomProductDialog } = useModal(ModalsKeys.ProposalCreateProductDialog);

  const dispatch = useDispatch();
  const lenderProfiles = useSelector((state: any) => state.lp);
  const builder = useSelector(deskingSelectors.builder);
  const financePricing = useSelector(deskingSelectors.financePrising);
  const commonProductsConfiguration = useSelector(deskingSelectors.commonProductsConfiguration);
  const isProposalsProductsLoading = useSelector<any, boolean>(deskingSelectors.isProposalsProductsLoading);
  const internalProposalDetailsForEdit = useSelector(deskingSelectors.proposalDetailsForEdit);
  const productsConfigInMenu = useSelector(deskingSelectors.proposalProductConfigurationsInMenu);
  const proposalMenuByCurrentTerm = useSelector(deskingSelectors.proposalMenuByCurrentTerm);
  const parentProposalId = useSelector(deskingSelectors.selectParentProposalId);

  const hasUnavailableProducts = useMemo(() => {
    const hasUnavailableProductsValue = !!proposalMenuByCurrentTerm?.menuOptions.some(mo => {
      return mo.products?.some(pp => pp?.aftermarketProduct?.productIsNoLongerAvailable === true);
    });
    return hasUnavailableProductsValue;
  }, [proposalMenuByCurrentTerm]);

  const { data: equipmentData, refetch: refetchEquipmentData } = useQuery<
    EquipmentsResponse<Equipment>,
    Partial<EquipmentsVariables>
  >(GET_EQUIPMENT, {
    skip: !vo?.vendorOpportunityId,
    notifyOnNetworkStatusChange: true,
    variables: { VOId: vo?.vendorOpportunityId },
    onCompleted(response) {
      if (!Array.isArray(response?.equipments)) return;
      dispatch(deskingActions.setEquipmentData({ equipments: response.equipments }));
    },
  });

  const formDefaultValues = getFormDefaultValues(
    vo,
    creditApplication,
    equipmentData?.equipments ?? [],
    allAvailableCcEmailOptions,
    userEmail,
    props.configurationByAccount?.saleMode?.saleType,
    props.configurationByAccount?.displaySettings
  );

  const form = useForm<FinanceProgramFormValues>({
    defaultValues: formDefaultValues,
  });

  const ccEmailOptionsToSet = allAvailableCcEmailOptions.filter(
    ({ email }) => email !== formDefaultValues.contact.email
  );

  const [ccEmailOptions, setCcEmailOptions] = useState<CcEmailOption[]>(ccEmailOptionsToSet);

  useEffect(() => {
    if (!invoice) return;

    const result = convertInvoiceToSaleDetailsFormValues(invoice);
    Object.entries(result).forEach(([key, value]) => form.setValue(`saleDetails.${key}` as any, value));
  }, [form, invoice]);

  const handleResetDesking = () => {
    form.reset(formDefaultValues);
    onChangeSaleType(SaleType.Financing);
    dispatch(deskingActions.resetStructureAndMenuOptions());
  };

  const handleSubmitForm = async ({ isDraft }: SubmitOptions) => {
    await onSubmitDesking({
      builder,
      isDraft,
      productsConfigInMenu,
      form: form.getValues(),
      pricing: financePricing,
      parentProposalId,
      commonProductsConfiguration,
      onDeskingReset: handleResetDesking,
    });
  };

  const handleCreateProduct = (refetchProposalProducts: () => Promise<ApolloQueryResult<ProposalProductsResponse>>) => {
    createCustomProductDialog({
      accountId,
      onCreate: async dto => {
        try {
          const newAftermarketProductId = await props.onCreateProposalProduct(dto);
          const { data } = await refetchProposalProducts();

          if (!newAftermarketProductId || !Array.isArray(data?.proposalProducts)) return newAftermarketProductId;

          const targetProduct = data.proposalProducts.find(
            ({ aftermarketProduct }) => aftermarketProduct?.aftermarketProductId === newAftermarketProductId
          );

          if (targetProduct) dispatch(deskingActions.addCustomProduct({ product: targetProduct }));

          return newAftermarketProductId;
        } catch (error) {
          logError(error);

          return null;
        }
      },
    });
  };

  const onFinanceProgramsLoaded = (financePrograms: FinanceProgram[]) => {
    const filteredPrograms = initialLenderProfileId
      ? financePrograms.filter(
          program => program.financeProgramToLenderProfiles?.[0]?.lenderProfileId === initialLenderProfileId
        )
      : financePrograms;

    dispatch(deskingActions.setFinancePrograms({ programs: filteredPrograms }));

    if (initialFinanceProgramId) {
      const programConfig =
        filteredPrograms.find(program => program.financeProgramId === initialFinanceProgramId)
          ?.paymentOptionsConfiguration ?? null;

      form.setValue('financeQuote.program', initialFinanceProgramId);
      form.setValue('financeQuote.createProposalWithExistingProgram', CreateProposalFinanceExistingOptions.Yes);

      if (programConfig.linkToLenderCreditApp && initialLenderProfileId) {
        form.setValue('displaySettings.lenderProfileId', initialLenderProfileId);
      }

      setProgramDeps({
        dispatch,
        setValue: form.setValue,
        programConfig,
      });
    }
  };

  return (
    <PanelFormStyled>
      <FormProvider {...form}>
        <ApiContainer
          accountId={accountId}
          location={creditApplication?.location}
          lenderProfiles={lenderProfiles}
          onFinanceProgramsLoaded={onFinanceProgramsLoaded}
        >
          {({ getEquipmentDataById, refetchProposalProducts }) => (
            <EditAdapter
              decisionId={decisionId}
              formDefaultValues={formDefaultValues}
              proposalSaleType={internalProposalDetailsForEdit?.saleType}
              proposalId={internalProposalDetailsForEdit?.id || proposalId}
            >
              <PanelForm
                vo={vo}
                accountId={accountId}
                invoiceAction={invoiceAction}
                customSections={customSections}
                ccEmailOptions={ccEmailOptions}
                formDefaultValues={formDefaultValues}
                configurationByAccount={configurationByAccount}
                onCreateProposalProduct={onCreateProposalProduct}
                formFieldsConfiguration={formFieldsConfiguration}
                refetchProposalProducts={refetchProposalProducts}
                isSupportBehaviorSetting={isSupportBehaviorSetting}
                isSupportSaleDetailsUpdating={isSupportSaleDetailsUpdating}
                externalDealInfo={externalDealInfo}
                actions={{
                  onEquipmentCreated: async equipmentId => {
                    await refetchEquipmentData();
                    if (actions?.onEquipmentCreated) await actions.onEquipmentCreated(equipmentId);

                    if (!vo?.vendorOpportunityId) return;

                    await getEquipmentDataById({ variables: { VOId: vo.vendorOpportunityId, id: equipmentId } });
                  },
                  onEquipmentUpdated: async equipmentId => {
                    await refetchEquipmentData();
                    if (actions?.onEquipmentUpdated) await actions.onEquipmentUpdated(equipmentId);
                  },
                  onSyncEquipment: async () => {
                    const { data } = await refetchEquipmentData();

                    dispatch(deskingActions.setEquipmentData({ equipments: data?.equipments }));

                    const id = data?.equipments?.[0]?.equipmentId;

                    if (!id || !vo?.vendorOpportunityId) return;

                    await getEquipmentDataById({ variables: { VOId: vo.vendorOpportunityId, id } });
                  },
                  onContactChanged: (customer: ContactForm) => {
                    const ccEmailPreSelectedValue = getCcEmailPreSelectedValue(
                      allAvailableCcEmailOptions,
                      customer.email,
                      vo,
                      userEmail
                    );
                    const ccEmailOptionsToSet = allAvailableCcEmailOptions.filter(o => o.email !== customer.email);
                    form.setValue('ccEmail', ccEmailPreSelectedValue);
                    setCcEmailOptions(ccEmailOptionsToSet);
                  },
                }}
                createProductSlot={
                  <RenderWithCondition condition={!!props.isProductCreationAvailable}>
                    <Button
                      startIcon={<Build />}
                      disabled={isProposalsProductsLoading}
                      onClick={() => handleCreateProduct(refetchProposalProducts)}
                    >
                      Add Manual
                    </Button>
                  </RenderWithCondition>
                }
              />

              <PanelFormActions
                onSubmitForm={handleSubmitForm}
                isProposalLoading={isProposalLoading}
                hasUnavailableProducts={hasUnavailableProducts}
              />
            </EditAdapter>
          )}
        </ApiContainer>
      </FormProvider>
    </PanelFormStyled>
  );
};
